"use client";

import React from "react";
import Link from "next/link";
import Icon from "@/components/Icon";

export interface FlatItem {
  label: string;
  href: string;
  icon: string;
}

export interface GroupedItem {
  groupLabel: string;
  groupColor: string;
  items: { label: string; href: string }[];
}

interface NavDropdownProps {
  items?: FlatItem[];
  groups?: GroupedItem[];
  isOpen: boolean;
  align?: "left" | "right";
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onItemClick: () => void;
  onEscape?: () => void;
}

export default function NavDropdown({
  items,
  groups,
  isOpen,
  align = "left",
  onMouseEnter,
  onMouseLeave,
  onItemClick,
  onEscape,
}: NavDropdownProps) {
  if (!isOpen) return null;

  return (
    <div
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onKeyDown={(e) => {
        if (e.key === "Escape" && onEscape) {
          e.preventDefault();
          onEscape();
        }
      }}
      className={`absolute top-full mt-0 bg-[var(--surface)] border-2 border-[var(--foreground)] shadow-lg p-5 z-[100] font-sans ${
        groups ? "w-[520px]" : "w-[260px]"
      } ${align === "right" ? "right-0" : "left-0"}`}
    >
      {/* Flat Mode Rendering */}
      {items && !groups && (
        <div className="flex flex-col gap-1.5 text-left">
          {items.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              onClick={onItemClick}
              prefetch={false}
              className="flex items-center gap-3 px-2.5 py-1.5 rounded hover:bg-[var(--surface-muted)] text-[12.5px] font-semibold text-[var(--ink)] hover:text-[var(--accent)] transition-colors group/item"
            >
              <span className="text-[var(--text-secondary)] group-hover/item:text-[var(--accent)] transition-colors shrink-0">
                <Icon name={item.icon} className="w-4 h-4" strokeWidth={1.6} />
              </span>
              <span>{item.label}</span>
            </Link>
          ))}
        </div>
      )}

      {/* Grouped Mode Rendering */}
      {groups && (
        <div className="grid grid-cols-3 gap-6 text-left">
          {groups.map((group) => (
            <div key={group.groupLabel} className="flex flex-col gap-2.5">
              {/* Header */}
              <span
                className="text-[10px] font-mono font-black uppercase tracking-wider border-b pb-1.5 border-[var(--line)]"
                style={{ color: group.groupColor }}
              >
                {group.groupLabel}
              </span>
              {/* Items */}
              <div className="flex flex-col gap-1.5">
                {group.items.map((item) => (
                  <Link
                    key={item.href}
                    href={item.href}
                    onClick={onItemClick}
                    prefetch={false}
                    className="px-2.5 py-1.5 rounded hover:bg-[var(--surface-muted)] text-[12px] font-semibold text-[var(--ink-soft)] hover:text-[var(--accent)] transition-colors block text-left"
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
